import React, { useEffect, useMemo, useState, Suspense } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Building2, User, ShieldCheck, MapPin, Sparkles, HeartHandshake } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { AUTH } from "@/constants/testIds";
import { getPillar, US_STATES } from "@/data/pillars";

const CoverageMap = React.lazy(() => import("@/components/CoverageMap"));

export default function AuthPage() {
    const [params] = useSearchParams();
    const nav = useNavigate();
    const { user, loginWith, registerAgency, registerCaregiver, registerFamily } = useAuth();

    const [mode, setMode] = useState(params.get("mode") === "login" ? "login" : "register");
    // role=family from home-health pillar keeps a real 'family' role now
    const initialRole = (() => {
        const r = params.get("role");
        if (r === "caregiver") return "caregiver";
        if (r === "family") return "family";
        return "agency";
    })();
    const [role, setRole] = useState(initialRole);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");

    // Pillar context (from /services flow)
    const pillarKey = params.get("pillar");
    const serviceId = params.get("service");
    const stateCode = params.get("state");
    const pillar = pillarKey ? getPillar(pillarKey) : null;
    const service = pillar?.services.find((s) => s.id === serviceId);
    const stateName = stateCode ? (US_STATES.find(([c]) => c === stateCode)?.[1] || stateCode) : null;

    useEffect(() => {
        if (pillarKey) {
            try {
                localStorage.setItem(
                    "careco_signup_context",
                    JSON.stringify({ pillar: pillarKey, service: serviceId, state: stateCode })
                );
            } catch { /* noop */ }
        }
    }, [pillarKey, serviceId, stateCode]);

    const [form, setForm] = useState({
        email: "", password: "", full_name: "", phone: "",
        agency_name: "", ein: "",
        credentials: [], state: stateCode || "",
        home_zip: "", willing_zips: "", willing_radius_miles: 25,
    });

    useEffect(() => {
        if (user) nav("/dashboard");
    }, [user, nav]);

    function set(k, v) { setForm((f) => ({ ...f, [k]: v })); }

    function toggleCredential(c) {
        setForm((f) => {
            const has = f.credentials.includes(c);
            return {
                ...f,
                credentials: has
                    ? f.credentials.filter((x) => x !== c)
                    : [...f.credentials, c],
            };
        });
    }

    const einValid = useMemo(() => /^\d{2}-\d{7}$/.test(form.ein), [form.ein]);

    async function submit(e) {
        e.preventDefault();
        setError("");
        setBusy(true);
        try {
            if (mode === "login") {
                await loginWith(form.email, form.password);
                toast.success("Welcome back");
                nav("/dashboard");
                return;
            }
            // Phone required for every registration flow — validate first
            const phoneDigits = (form.phone || "").replace(/\D/g, "");
            if (phoneDigits.length < 10) {
                setError("Phone number is required (10+ digits)");
                setBusy(false);
                return;
            }
            if (role === "agency") {
                if (!einValid) { setError("EIN must match XX-XXXXXXX"); setBusy(false); return; }
                await registerAgency({
                    email: form.email, password: form.password,
                    full_name: form.full_name, agency_name: form.agency_name,
                    ein: form.ein, phone: form.phone,
                });
            } else if (role === "caregiver") {
                if (!form.credentials?.length) { setError("Please select at least one credential (RN / LPN / CNA / HHA / Other) — you can pick more than one"); setBusy(false); return; }
                if (!form.state) { setError("Please pick the state where you'll work"); setBusy(false); return; }
                if (form.home_zip && !/^\d{5}$/.test(form.home_zip)) { setError("Home ZIP must be 5 digits"); setBusy(false); return; }
                const willing = (form.willing_zips || "")
                    .split(/[,\s]+/)
                    .map((z) => z.trim())
                    .filter((z) => /^\d{5}$/.test(z));
                await registerCaregiver({
                    email: form.email, password: form.password,
                    full_name: form.full_name,
                    credential: form.credentials[0],
                    credentials: form.credentials,
                    state: form.state,
                    home_zip: form.home_zip || undefined,
                    willing_zips: willing,
                    willing_radius_miles: form.willing_radius_miles,
                    phone: form.phone,
                    ref: params.get("ref") || undefined,
                });
            } else if (role === "family") {
                await registerFamily({
                    email: form.email, password: form.password,
                    full_name: form.full_name,
                    care_recipient_name: form.care_recipient_name || undefined,
                    family_state: stateCode || undefined,
                    phone: form.phone,
                });
            }
            toast.success("Account created");
            nav("/dashboard");
        } catch (err) {
            const msg = err?.response?.data?.detail || err.message || "Something went wrong";
            setError(typeof msg === "string" ? msg : JSON.stringify(msg));
        } finally {
            setBusy(false);
        }
    }

    return (
        <div className="min-h-screen bg-stone-50 flex items-center justify-center p-4">
            <div className="w-full max-w-5xl grid lg:grid-cols-5 gap-8 items-stretch">
                <div className="hidden lg:flex lg:col-span-2 rounded-3xl overflow-hidden relative bg-emerald-800 text-white p-10 flex-col justify-between grain-overlay">
                    <div>
                        <Link to="/" className="font-outfit text-2xl font-semibold tracking-tight">Infini Health<span className="text-emerald-200">Care</span></Link>
                        <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-emerald-200/70 mt-2">by CareCo 24</div>
                    </div>
                    <div>
                        <ShieldCheck className="w-8 h-8 text-orange-300 mb-4" strokeWidth={1.5} />
                        <h2 className="font-outfit text-3xl font-semibold tracking-tight leading-tight">
                            EIN-verified, payout-ready, care-first.
                        </h2>
                        <p className="mt-4 text-emerald-100/90 text-sm font-dmsans">
                            Agencies get Stripe Connect Express onboarding. Caregivers get auto-parsed resumes and calendar sync. Everyone gets the encrypted vault.
                        </p>
                    </div>
                    <div className="text-xs text-emerald-200 font-dmsans">Encrypted at rest · Signed URLs · Role-based access</div>
                </div>

                <Card className="lg:col-span-3 rounded-3xl border-stone-200 shadow-sm">
                    <CardContent className="p-8 lg:p-10">
                        <div className="mb-6">
                            <h1 className="font-outfit text-3xl font-semibold tracking-tight text-stone-900">
                                {mode === "login" ? "Sign in" : "Create your account"}
                            </h1>
                            <p className="text-sm text-stone-500 mt-1 font-dmsans">
                                {mode === "login" ? "Welcome back." : "Onboard in under 60 seconds."}
                            </p>
                        </div>

                        {pillar && mode === "register" && (
                            <div
                                data-testid="auth-pillar-context"
                                className="mb-6 p-4 rounded-2xl bg-emerald-50 border border-emerald-100"
                            >
                                <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-emerald-700 mb-1">
                                    <Sparkles className="w-3.5 h-3.5" /> You&rsquo;re signing up for
                                </div>
                                <div className="text-sm text-stone-800 font-dmsans">
                                    <span className="font-medium">{pillar.title}</span>
                                    {service && <span> · {service.label}</span>}
                                    {stateName && (
                                        <span className="inline-flex items-center gap-1 ml-1 text-stone-600">
                                            <MapPin className="w-3.5 h-3.5" /> {stateName}
                                        </span>
                                    )}
                                </div>
                            </div>
                        )}

                        {mode === "register" && (
                            <Tabs value={role} onValueChange={setRole} className="mb-6">
                                <TabsList className="grid grid-cols-3 rounded-full bg-stone-100 p-1 h-12">
                                    <TabsTrigger
                                        value="family"
                                        data-testid="auth-tab-family"
                                        className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm text-xs sm:text-sm"
                                    >
                                        <HeartHandshake className="w-4 h-4 mr-1.5" /> Family
                                    </TabsTrigger>
                                    <TabsTrigger
                                        value="agency"
                                        data-testid={AUTH.tabAgency}
                                        className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm text-xs sm:text-sm"
                                    >
                                        <Building2 className="w-4 h-4 mr-1.5" /> Agency
                                    </TabsTrigger>
                                    <TabsTrigger
                                        value="caregiver"
                                        data-testid={AUTH.tabCaregiver}
                                        className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm text-xs sm:text-sm"
                                    >
                                        <User className="w-4 h-4 mr-1.5" /> Nurses &amp; Caregivers
                                    </TabsTrigger>
                                </TabsList>
                                <TabsContent value="family" />
                                <TabsContent value="agency" />
                                <TabsContent value="caregiver" />
                            </Tabs>
                        )}

                        <form onSubmit={submit} className="space-y-4">
                            {mode === "register" && (
                                <div>
                                    <Label>Full name</Label>
                                    <Input
                                        data-testid={AUTH.fullNameInput}
                                        value={form.full_name}
                                        onChange={(e) => set("full_name", e.target.value)}
                                        required
                                        className="mt-1.5 h-12 rounded-xl"
                                        placeholder="Jane Doe"
                                    />
                                </div>
                            )}

                            {mode === "register" && role === "caregiver" && (
                                <div>
                                    <Label>Your credentials <span className="text-orange-600">*required</span> <span className="text-stone-500 font-normal">(pick one or more)</span></Label>
                                    <div className="mt-2 grid grid-cols-5 gap-2">
                                        {["RN", "LPN", "CNA", "HHA", "OTHER"].map((c) => {
                                            const active = form.credentials.includes(c);
                                            return (
                                                <button
                                                    key={c}
                                                    type="button"
                                                    data-testid={`auth-credential-${c.toLowerCase()}`}
                                                    data-active={active}
                                                    aria-pressed={active}
                                                    onClick={() => toggleCredential(c)}
                                                    className="relative h-11 rounded-xl border-2 border-stone-200 hover:border-emerald-500 hover:bg-emerald-50 data-[active=true]:bg-emerald-700 data-[active=true]:text-white data-[active=true]:border-emerald-700 font-outfit font-semibold text-sm transition-all"
                                                >
                                                    {c}
                                                    {active && (
                                                        <span aria-hidden className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-white text-emerald-700 border border-emerald-700 text-[10px] leading-4 text-center">✓</span>
                                                    )}
                                                </button>
                                            );
                                        })}
                                    </div>
                                    <p className="text-xs text-stone-500 mt-1.5 font-dmsans">
                                        RN = Registered Nurse · LPN = Licensed Practical Nurse · CNA = Certified Nursing Assistant · HHA = Home Health Aide. You can pick every credential you hold — families and facilities that require any of them will see you.
                                    </p>

                                    {form.credentials.length > 0 && (
                                        <div className="mt-4">
                                            <Label>
                                                State where you&rsquo;ll work <span className="text-orange-600">*required</span>
                                            </Label>
                                            <p className="text-xs text-stone-500 mt-1 font-dmsans">
                                                You&rsquo;ll be added to that state&rsquo;s Labor Pool so families and agencies nearby can find you.
                                            </p>
                                            <select
                                                data-testid="auth-caregiver-state"
                                                value={form.state}
                                                onChange={(e) => set("state", e.target.value)}
                                                className="mt-2 h-12 w-full rounded-xl border border-stone-200 bg-white px-3 text-sm font-dmsans focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100 outline-none"
                                            >
                                                <option value="">Choose your state…</option>
                                                {US_STATES.map(([code, name]) => (
                                                    <option key={code} value={code}>{name} ({code})</option>
                                                ))}
                                            </select>

                                            <div className="mt-4 grid gap-3 sm:grid-cols-[140px,1fr]">
                                                <div>
                                                    <Label>Home ZIP</Label>
                                                    <Input
                                                        data-testid="auth-caregiver-home-zip"
                                                        value={form.home_zip}
                                                        onChange={(e) => set("home_zip", e.target.value.replace(/[^0-9]/g, "").slice(0, 5))}
                                                        placeholder="90210"
                                                        inputMode="numeric"
                                                        maxLength={5}
                                                        className="h-11 rounded-xl mt-1"
                                                    />
                                                </div>
                                                <div>
                                                    <Label>Other ZIPs you&rsquo;ll cover <span className="text-stone-400 text-xs">(optional)</span></Label>
                                                    <Input
                                                        data-testid="auth-caregiver-willing-zips"
                                                        value={form.willing_zips}
                                                        onChange={(e) => set("willing_zips", e.target.value)}
                                                        placeholder="90211, 90212, 91001"
                                                        className="h-11 rounded-xl mt-1"
                                                    />
                                                </div>
                                            </div>

                                            <div className="mt-4">
                                                <Label>Distance you&rsquo;ll travel from your Home ZIP</Label>
                                                <div className="mt-1.5 flex flex-wrap gap-2" role="group" aria-label="Coverage radius">
                                                    {[
                                                        { value: 10, label: "10 mi" },
                                                        { value: 25, label: "25 mi" },
                                                        { value: 50, label: "50 mi" },
                                                        { value: 100, label: "100 mi" },
                                                        { value: 9999, label: "Nationwide" },
                                                    ].map((r) => {
                                                        const active = form.willing_radius_miles === r.value;
                                                        return (
                                                            <button
                                                                key={r.value}
                                                                type="button"
                                                                data-testid={`auth-caregiver-radius-${r.value}`}
                                                                data-active={active}
                                                                aria-pressed={active}
                                                                onClick={() => set("willing_radius_miles", r.value)}
                                                                className="h-10 px-4 rounded-full border-2 border-stone-200 hover:border-emerald-500 hover:bg-emerald-50 data-[active=true]:bg-emerald-700 data-[active=true]:text-white data-[active=true]:border-emerald-700 font-outfit font-semibold text-xs sm:text-sm transition-all"
                                                            >
                                                                {r.label}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                                <p className="text-[11px] text-stone-500 mt-1.5 font-dmsans">
                                                    {form.willing_radius_miles === 9999
                                                        ? "You'll be shown open shifts anywhere in the U.S."
                                                        : `We'll show you open shifts within ${form.willing_radius_miles} miles of your Home ZIP.`}
                                                </p>

                                                {/* Interactive coverage map */}
                                                <div className="mt-3">
                                                    <Suspense fallback={<div className="h-[220px] rounded-xl border border-stone-200 bg-stone-50 flex items-center justify-center text-xs text-stone-500 font-dmsans">Loading map…</div>}>
                                                        <CoverageMap
                                                            zip={form.home_zip}
                                                            radiusMiles={form.willing_radius_miles}
                                                        />
                                                    </Suspense>
                                                </div>
                                            </div>

                                            <p className="text-[11px] text-stone-500 mt-3 font-dmsans">
                                                We&rsquo;ll only email you open-shift alerts within your chosen radius (plus any specific ZIPs above). Change this anytime from your profile.
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}

                            {mode === "register" && role === "family" && (
                                <div>
                                    <Label>Care recipient name <span className="text-stone-400 text-xs">(optional)</span></Label>
                                    <Input
                                        data-testid="auth-care-recipient-input"
                                        value={form.care_recipient_name}
                                        onChange={(e) => set("care_recipient_name", e.target.value)}
                                        className="mt-1.5 h-12 rounded-xl"
                                        placeholder="e.g. Mom, Dad, John Sr."
                                    />
                                </div>
                            )}

                            {mode === "register" && role === "agency" && (
                                <div className="grid sm:grid-cols-2 gap-4">
                                    <div>
                                        <Label>Agency name</Label>
                                        <Input
                                            data-testid={AUTH.agencyNameInput}
                                            value={form.agency_name}
                                            onChange={(e) => set("agency_name", e.target.value)}
                                            required
                                            className="mt-1.5 h-12 rounded-xl"
                                            placeholder="Bright Care LLC"
                                        />
                                    </div>
                                    <div>
                                        <Label>EIN <span className="text-orange-600">*required</span></Label>
                                        <Input
                                            data-testid={AUTH.einInput}
                                            value={form.ein}
                                            onChange={(e) => set("ein", e.target.value)}
                                            required
                                            className={`mt-1.5 h-12 rounded-xl ${form.ein && !einValid ? "border-red-300" : ""}`}
                                            placeholder="12-3456789"
                                        />
                                        {form.ein && !einValid && (
                                            <p className="text-xs text-red-600 mt-1">Format XX-XXXXXXX</p>
                                        )}
                                    </div>
                                </div>
                            )}

                            <div className="grid sm:grid-cols-2 gap-4">
                                <div>
                                    <Label>Email</Label>
                                    <Input
                                        data-testid={AUTH.emailInput}
                                        type="email"
                                        value={form.email}
                                        onChange={(e) => set("email", e.target.value)}
                                        required
                                        className="mt-1.5 h-12 rounded-xl"
                                        placeholder="you@work.com"
                                    />
                                </div>
                                {mode === "register" && (
                                    <div>
                                        <Label>Phone <span className="text-orange-600 text-xs">*required</span></Label>
                                        <Input
                                            data-testid={AUTH.phoneInput}
                                            value={form.phone}
                                            onChange={(e) => set("phone", e.target.value)}
                                            className="mt-1.5 h-12 rounded-xl"
                                            placeholder="+1 555 0100"
                                            required
                                            inputMode="tel"
                                            autoComplete="tel"
                                        />
                                        <p className="text-[11px] text-stone-500 font-dmsans mt-1">
                                            Needed for urgent shift updates + verification. We only text you about the shifts you agreed to.
                                        </p>
                                    </div>
                                )}
                            </div>

                            <div>
                                <Label>Password</Label>
                                <Input
                                    data-testid={AUTH.passwordInput}
                                    type="password"
                                    value={form.password}
                                    onChange={(e) => set("password", e.target.value)}
                                    required
                                    minLength={8}
                                    className="mt-1.5 h-12 rounded-xl"
                                    placeholder="At least 8 characters"
                                />
                            </div>

                            {error && (
                                <div data-testid={AUTH.error} className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-4 py-3">
                                    {error}
                                </div>
                            )}

                            <Button
                                data-testid={AUTH.submitBtn}
                                type="submit"
                                disabled={busy}
                                className="w-full h-12 rounded-full bg-emerald-700 hover:bg-emerald-800 text-white text-base font-medium"
                            >
                                {busy ? "..." : mode === "login" ? "Sign in" : role === "agency" ? "Create agency account" : "Create caregiver account"}
                            </Button>

                            {mode === "login" && (
                                <div className="text-center">
                                    <Link
                                        to="/forgot-password"
                                        data-testid="auth-forgot-link"
                                        className="text-sm text-emerald-700 hover:underline font-dmsans"
                                    >
                                        Forgot password?
                                    </Link>
                                </div>
                            )}

                            <div className="text-center text-sm text-stone-500 font-dmsans">
                                {mode === "login" ? (
                                    <>New here?{" "}
                                        <button
                                            type="button"
                                            data-testid={AUTH.modeRegister}
                                            onClick={() => setMode("register")}
                                            className="text-emerald-700 font-medium hover:underline"
                                        >
                                            Create an account
                                        </button>
                                    </>
                                ) : (
                                    <>Already have an account?{" "}
                                        <button
                                            type="button"
                                            data-testid={AUTH.modeLogin}
                                            onClick={() => setMode("login")}
                                            className="text-emerald-700 font-medium hover:underline"
                                        >
                                            Sign in
                                        </button>
                                    </>
                                )}
                            </div>
                        </form>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
